(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('./enums/dataSourceIds.js'),
            require('./dataSources.js'),
            require('./metricRequest.js'),
            require('lodash')
        );

    } else {
        RivalIQ.module('metrics.new', function (metricsNew) {
            metricsNew.MetricRegistry = exportFn(
                RivalIQ.metrics.new.enums.DataSourceIds,
                RivalIQ.metrics.new.DataSources,
                RivalIQ.metrics.new.MetricRequest,
                window._
            );
        });
    }

    function exportFn(DataSourceIds, DataSources, MetricRequest, _) {

        const MetricRegistry = {};
        const TIME_DIMENSIONS = ['day', 'week', 'month', 'hourOfDay', 'dayOfWeek'];
        const DIMENSION_NAMES = {
            'companyId': 'Company'
        };

        MetricRegistry.getMetadataFromDataSource = function (dataSourceId, attribute) {
            if (!DataSources[dataSourceId]) {
                throw new Error(`No dataSource is defined for dataSourceId: ${dataSourceId}`);
            }
            const dataSource = DataSources[dataSourceId];
            if (_.isUndefined(dataSource[attribute])) {
                throw new Error(`${attribute} is not defined for dataSourceId: ${dataSourceId}`);
            }
            return dataSource[attribute];
        };

        function hasNameException(metric) {
            // TODO match metric again data source's name exception list
            if (_.lowerCase(getDimensionNamesFromMetric(metric)) === 'hashtag') {
                return getFieldNameFromMetric(metric);
            }
            return false;
        }

        function shouldPluralize(metric) {
            return _.includes(metric.dimensions, 'postId') || _.get(metric, ['measure', 'field']) === 'postId';
        }

        function getTweetNameFromMetric(metric, excludeTweetType) {
            // Field names should include tweet types. EX: Retweet Tweets, All Tweets, etc.
            // dimension and reducer names should exclude tweet types since those follower the 'per' in
            // the metric name, ie. Eng Total / Tweet not Eng Total / Retweet Tweets
            const tweetTypes = _.get(metric, ['filters', 'publicPost', 'tweetTypes']);
            const pluralSuffix = shouldPluralize(metric) ? 's' : '';
            const hasSingleTweetType = _.size(tweetTypes) === 1;
            if (excludeTweetType || (hasSingleTweetType && _.first(tweetTypes) === 'normal')) {
                return `Tweet${pluralSuffix}`;
            } else if (hasSingleTweetType && _.first(tweetTypes) === 'reply') {
                return `Reply Tweet${pluralSuffix}`;
            } else if (hasSingleTweetType && _.first(tweetTypes) === 'retweet') {
                return `Retweet Tweet${pluralSuffix}`;
            } else if (_.size(tweetTypes) === 3) {
                return `All Tweet${pluralSuffix}`;
            }

            return `Tweet${pluralSuffix}`;
        }

        function getPostNameFromMetric(metric, excludeTweetType) {
            if (metric.dataSourceId === DataSourceIds.TWITTER_PUBLIC_POST_DATA) {
                return getTweetNameFromMetric(metric, excludeTweetType);
            }

            const pluralSuffix = shouldPluralize(metric) ? 's' : '';
            const name = MetricRegistry.getMetadataFromDataSource(metric.dataSourceId, 'postName');
            return _.capitalize(`${name}${pluralSuffix}`);
        }

        function getFieldNameFromMetric(metric) {
            if (metric.measure.field === 'postId' || metric.measure.field === 'post_id') {
                return getPostNameFromMetric(metric, false);
            }

            const dataSourceFields = MetricRegistry.getMetadataFromDataSource(metric.dataSourceId, 'fields');
            return _.get(dataSourceFields, [metric.measure.field, 'name']);
        }

        function getReducerNameFromMetric(metric) {
            const reducer = _.get(metric, ['___expandedDimensionReductions', 0, 'reducer']);
            switch (reducer) {
                case 'averagePerDay':
                    return 'Day';

                case 'averagePerWeek':
                    return 'Week';

                case 'average':
                    // if the metric's last dimension is a period of time, the metric name after 'per' will be
                    // determined by the dimensionName and we can fall through here. ex Eng Total / Day
                    // {
                    //     dimensions: ['postId', 'day'],
                    //     measure: 'average(engagement_total)'
                    // }
                    if (_.includes(TIME_DIMENSIONS, _.last(metric.dimensions))) {
                        return '';
                    }
                    return getPostNameFromMetric(metric, true);

                case 'averagePerCompanyPerPeriod':
                    return 'Average';

                case 'aggregateLift':
                case 'averageRate':
                case 'distinctCount':
                case 'latestNonNull':
                case 'sum':
                case undefined:

                    return '';

                default:
                    throw new Error(`Reducer ${reducer} is not supported`);
            }
        }

        function getDimensionNamesFromMetric(metric) {
            const lastDimension = _.last(metric.dimensions);
            switch (lastDimension) {
                case 'day':
                case 'week':
                case 'month':
                case 'hashtag':
                    return _.capitalize(lastDimension);
                case 'dayOfWeek':
                case 'hourOfDay':
                    return 'Daily';
                case 'postTagId':
                    return 'Post Tag';
                case 'mediaType':
                    return 'Type';
                default:
                    return '';
            }
        }

        // Name variations:
        // <channel> <metric name> (default)
        // <metric name>
        // there's an additional option to use '/' or spell out 'per' for rate metrics
        //  - <metric> / <denominator> (default)
        //  - <metric> per <denominator>
        MetricRegistry.getName = function (metricRequest, redactChannelName, spellOutName) {
            const canonicalizedMetric = MetricRequest.canonicalizeMetricRequest(metricRequest);

            if (canonicalizedMetric.___returns?.operator === 'dimensionValue') {
                const dimensionName = canonicalizedMetric.___returns.value;
                return DIMENSION_NAMES[dimensionName] || dimensionName;
            }

            // search through the expection list for a potential match
            const nameException = hasNameException(canonicalizedMetric);
            if (nameException) {
                return nameException;
            }

            let channelName = '';
            if (metricRequest.dataSourceId !== DataSourceIds.COMPANIES && !redactChannelName) {
                channelName = MetricRegistry.getMetadataFromDataSource(canonicalizedMetric.dataSourceId, 'channelName');
            }

            const fieldName = getFieldNameFromMetric(canonicalizedMetric);
            const reducerName = getReducerNameFromMetric(canonicalizedMetric);
            const dimensionNames = getDimensionNamesFromMetric(canonicalizedMetric);

            let name;
            if (dimensionNames || reducerName) {
                if (spellOutName) {
                    name = `${fieldName} per ${dimensionNames}${reducerName}`;
                } else {
                    name = `${fieldName} / ${dimensionNames}${reducerName}`;
                }
            } else {
                name = `${fieldName}`;
            }

            return `${redactChannelName ? '' : `${channelName} `}${name}`;
        };

        return MetricRegistry;
    }
})();
